/**
 * useContentPublications — single-strategy real-time publications
 * subscription. Used by calendar Mode A (single strategy, full span).
 *
 * Exposes CRUD wrappers over ContentPublicationFirestoreService so
 * components can drag-drop and add/edit without binding directly to
 * Firestore methods.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { ContentPublicationFirestoreService } from '../services/ContentPublicationFirestoreService';
import type { ContentPublication } from '../types';

export interface UseContentPublicationsReturn {
  publications: ContentPublication[];
  loading: boolean;
  error: Error | null;
  create: (data: Omit<ContentPublication, 'id' | 'projectId' | 'strategyId' | 'createdAt' | 'updatedAt'>) => Promise<string>;
  update: (pubId: string, patch: Partial<Omit<ContentPublication, 'id' | 'projectId' | 'strategyId'>>) => Promise<void>;
  move: (pubId: string, schedule: Pick<ContentPublication, 'scheduledAt' | 'weekIndex' | 'weekdayIndex'>) => Promise<void>;
  remove: (pubId: string) => Promise<void>;
}

export function useContentPublications(
  projectId: string | null,
  strategyId: string | null,
): UseContentPublicationsReturn {
  const [publications, setPublications] = useState<ContentPublication[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const service = useMemo(() => new ContentPublicationFirestoreService(), []);

  useEffect(() => {
    if (!projectId || !strategyId) {
      setPublications([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const unsub = service.subscribe(projectId, strategyId, (pubs) => {
      setPublications(pubs);
      setLoading(false);
      setError(null);
    });
    return () => unsub();
  }, [projectId, strategyId, service]);

  const create = useCallback(
    async (data: Omit<ContentPublication, 'id' | 'projectId' | 'strategyId' | 'createdAt' | 'updatedAt'>) => {
      if (!projectId || !strategyId) throw new Error('useContentPublications.create: missing projectId or strategyId');
      return service.create(projectId, strategyId, { ...data, projectId, strategyId });
    },
    [projectId, strategyId, service],
  );

  const update = useCallback(
    async (pubId: string, patch: Partial<Omit<ContentPublication, 'id' | 'projectId' | 'strategyId'>>) => {
      if (!projectId || !strategyId) throw new Error('useContentPublications.update: missing projectId or strategyId');
      await service.update(projectId, strategyId, pubId, patch);
    },
    [projectId, strategyId, service],
  );

  const move = useCallback(
    async (pubId: string, schedule: Pick<ContentPublication, 'scheduledAt' | 'weekIndex' | 'weekdayIndex'>) => {
      if (!projectId || !strategyId) throw new Error('useContentPublications.move: missing projectId or strategyId');
      await service.move(projectId, strategyId, pubId, schedule);
    },
    [projectId, strategyId, service],
  );

  const remove = useCallback(
    async (pubId: string) => {
      if (!projectId || !strategyId) throw new Error('useContentPublications.remove: missing projectId or strategyId');
      await service.delete(projectId, strategyId, pubId);
    },
    [projectId, strategyId, service],
  );

  return { publications, loading, error, create, update, move, remove };
}
